'use client';

import { useEffect, useState } from 'react';
import {
  useBreakpointValue,
  Box,
  Flex,
  Text,
  Button,
  VStack,
  LucideIcon,
  HStack,
  Image,
} from '@skin-seoul/design-system';
import { LoggingImpression, LoggingClick, useCallbackRef } from '@skin-seoul/utils';
import { useReviewService } from '@skin-seoul/service';
import type { ProductDetail, Review as ServiceReview } from '@skin-seoul/service';
import ReviewCard from './components/ReviewCard';
import ReviewFilterBar, { sortOptions } from './components/ReviewFilterBar';
import ReviewHeader from './components/ReviewHeader';
import LoadMoreButton from './components/LoadMoreButton';

import { ReviewWriteMobileModal } from './components/ReviewWriteMobileModal';
import { ReviewWritePcModal } from './components/ReviewWritePcModal';
import ReviewDetailModal from './components/ReviewDetailModal';

export { ReviewWriteMobileModal } from './components/ReviewWriteMobileModal';
export { ReviewWritePcModal } from './components/ReviewWritePcModal';

import type { ComponentProps } from 'react';
import ReviewImageGallery from './components/ReviewImageGallery';
import ReviewEmpty from './components/ReviewEmpty';
import { cleanSkinData } from '@skin-seoul/service/src/utils/review/taxonomyUtils';
import type { ReviewFormData } from '@skin-seoul/service';
import reviewPreviewDesktop from './assets/images/review-preview-desktop.png';
import reviewPreviewMobile from './assets/images/review-preview-mobile.png';

const LOAD_MORE_REVIEW_LIMIT_COUNT = 8;

type BoxProps = ComponentProps<typeof Box>;

const Container = ({ children, ...props }: { children: React.ReactNode } & Omit<BoxProps, 'children'>) => (
  <Box
    w="full"
    bg="#ffffff"
    fontFamily="General Sans, sans-serif"
    boxSizing="border-box"
    maxW={{ base: 'full', tablet: '1280px' }}
    mx={{ base: '0', tablet: 'auto' }}
    {...props}
  >
    {children}
  </Box>
);

const Divider = ({ ...props }: BoxProps) => (
  <Box border="none" h="1px" bg="#e2e2e2" my={{ base: '24px', tablet: '32px' }} {...props} />
);

export interface ReviewImage {
  id: string;
  url: string;
  alt: string;
  reviewId: string;
}

export interface Review {
  id: string;
  rating: number;
  title: string;
  content: string;
  author: {
    id: string;
    name: string;
    avatar?: string;
  };
  verifiedPurchase: boolean;
  createdAt: string;
  likeCount: number;
  skinType: string[];
  skinConcern: string[];
  images?: ReviewImage[];
}

function mapServiceReviewToReview(r: ServiceReview): Review {
  return {
    id: String(r.reviewId),
    rating: r.rating,
    title: r.title,
    content: r.body,
    author: {
      id: '',
      name: r.name,
      avatar: '',
    },
    verifiedPurchase: true,
    createdAt: r.modifiedAt,
    likeCount: 0,
    skinType: cleanSkinData(r.skinType),
    skinConcern: cleanSkinData(r.skinConcern),
    images: r.pictureUrls.map((url, idx) => ({
      id: `${r.reviewId}_${idx}`,
      url,
      alt: r.title,
      reviewId: String(r.reviewId),
    })),
  };
}

interface ReviewSectionProps {
  product: ProductDetail;
  locale?: string;
}

export const ReviewSection = ({ product, locale }: ReviewSectionProps) => {
  const [displayedReviewsCount, setDisplayedReviewsCount] = useState(LOAD_MORE_REVIEW_LIMIT_COUNT);
  const [isPcWriteModalOpen, setIsPcWriteModalOpen] = useState(false);
  const [isMobileWriteOpen, setIsMobileWriteOpen] = useState(false);
  const [isImageGalleryOpen, setIsImageGalleryOpen] = useState(false);
  const [imageGalleryInitialState, setImageGalleryInitialState] = useState<{
    selectedReview: Review | null;
    selectedImageId: string | null;
  }>({
    selectedReview: null,
    selectedImageId: null,
  });

  const [sort, setSort] = useState<string>(sortOptions.at(0)?.value || '');
  const [skinTypes, setSkinTypes] = useState<string[]>([]);
  const [skinConcerns, setSkinConcerns] = useState<string[]>([]);
  const isPC = useBreakpointValue({ base: false, tablet: true });
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showAiReviews, setShowAiReviews] = useState(false);

  const reviewService = useReviewService();
  const { useGetProductReviews, useGetAIProductReviews, useCreateReview } = reviewService;

  const { mutate: createReview, isPending: isCreatingReview } = useCreateReview();

  const { data: reviewsRaw = [], isLoading: isReviewsLoading } = useGetProductReviews(Number(product.productId), {
    locale,
  });
  const { data: aiReviewsRaw = [], isFetching: isFetchingAIReviews } = useGetAIProductReviews(
    Number(product.productId),
    { locale },
  );

  const reviews = reviewsRaw.map(mapServiceReviewToReview);
  const aiReviews = aiReviewsRaw.map(mapServiceReviewToReview);

  const customerReviewImages = reviewsRaw.flatMap((r) =>
    r.pictureUrls.map((url, idx) => ({
      id: `${r.reviewId}_${idx}`,
      url,
      alt: r.title,
      reviewId: String(r.reviewId),
    })),
  );

  const sortedReviews = (() => {
    switch (sort) {
      case 'newest':
        return [...reviews].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      case 'oldest':
        return [...reviews].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      case 'highest':
        return [...reviews].sort((a, b) => b.rating - a.rating);
      case 'lowest':
        return [...reviews].sort((a, b) => a.rating - b.rating);
      default:
        return reviews;
    }
  })();

  const sortedAndFilteredReviews = sortedReviews
    .filter((review) => skinTypes.length === 0 || skinTypes.some((type) => review.skinType.includes(type)))
    .filter(
      (review) => skinConcerns.length === 0 || skinConcerns.some((concern) => review.skinConcern.includes(concern)),
    );

  const totalReviewsLength = reviews.length;

  const displayedReviews = sortedAndFilteredReviews.slice(0, displayedReviewsCount);

  const hasMoreReviews = displayedReviewsCount < sortedAndFilteredReviews.length;
  const hasShownAllReviews = !hasMoreReviews;

  const handleLoadMore = useCallbackRef(() => {
    setDisplayedReviewsCount((prev) => prev + LOAD_MORE_REVIEW_LIMIT_COUNT);
  });

  // Reset displayed count when filters change
  useEffect(() => {
    setDisplayedReviewsCount(LOAD_MORE_REVIEW_LIMIT_COUNT);
  }, [sort, skinTypes, skinConcerns]);

  const averageRating =
    totalReviewsLength === 0
      ? 0
      : Number((reviews.reduce((sum, r) => sum + r.rating, 0) / totalReviewsLength).toFixed(1));

  const ratingDistribution: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  reviews.forEach((r) => {
    const rating = Math.round(r.rating);
    if (rating >= 1 && rating <= 5) {
      ratingDistribution[rating] += 1;
    }
  });

  const hasReviews = reviews.length > 0;
  const hasAiReviews = aiReviews.length > 0;

  const canShowAIButton =
    !isReviewsLoading && ((hasReviews && hasShownAllReviews) || !hasReviews) && hasAiReviews && !showAiReviews;

  const handleShowAIReviews = useCallbackRef(() => {
    setShowAiReviews(true);
  });

  useEffect(() => {
    if (!isPC && isMobileWriteOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileWriteOpen, isPC]);

  const handleOpenWriteModal = () => {
    if (isPC) {
      setIsPcWriteModalOpen(true);
    } else {
      setIsMobileWriteOpen(true);
    }
  };

  const handleCloseWriteModal = useCallbackRef(() => setIsPcWriteModalOpen(false));

  const handleCloseMobileWriteModal = useCallbackRef(() => setIsMobileWriteOpen(false));

  const handleSubmit = useCallbackRef((values: ReviewFormData) => {
    return new Promise<void>((resolve, reject) => {
      createReview(
        {
          name: values.nickname,
          rating: values.rating,
          body: values.content,
          productId: Number(product.productId),
          title: values.title,
          files: values.images,
          skinType: [values.skinType],
          skinConcern: [values.skinConcern],
          email: values.email,
        },
        {
          onSuccess: () => {
            resolve();
          },
          onError: (error) => {
            reject(new Error('Failed to create review: ' + (error?.message || 'Unknown error')));
          },
        },
      );
    });
  });

  const handleSortChange = (sortBy: string) => {
    setSort(sortBy);
  };

  const handleSkinTypeChange = (skinTypes: string[]) => {
    setSkinTypes(skinTypes);
  };

  const handleSkinConcernChange = (skinConcerns: string[]) => {
    setSkinConcerns(skinConcerns);
  };

  const handleImageClick = useCallbackRef((image: ReviewImage, _index: number) => {
    const review = reviews.find((r) => r.images?.some((img) => img.id === image.id));
    if (review) {
      setIsImageGalleryOpen(true);
      setImageGalleryInitialState({
        selectedReview: review,
        selectedImageId: image.id,
      });
    }
  });

  const handleViewAllImages = useCallbackRef(() => {
    setIsImageGalleryOpen(true);
  });

  const handleCloseImageGallery = useCallbackRef(() => {
    setIsImageGalleryOpen(false);
    setImageGalleryInitialState({
      selectedReview: null,
      selectedImageId: null,
    });
  });

  const handleViewMore = useCallbackRef((review: Review) => {
    setIsImageGalleryOpen(true);
    const firstImageId = review.images && review.images.length > 0 ? review.images[0].id : null;
    setImageGalleryInitialState({
      selectedReview: review,
      selectedImageId: firstImageId,
    });
  });

  // AI reviews are shown all at once, no pagination

  return (
    <Container id="judgeme_product_reviews">
      <VStack
        align="flex-start"
        gap={4}
        onClick={() => setIsCollapsed(!isCollapsed)}
        mb={{
          base: 6,
          tablet: 4,
        }}
      >
        <VStack align="flex-start" gap={6}>
          <VStack align="flex-start" gap={2}>
            <Text
              textStyle={{ base: 'paragraph.medium', tablet: 'heading.h6' }}
              fontWeight="medium"
              color="neutral.800"
              textAlign="left"
              whiteSpace="nowrap"
            >
              Ratings & Reviews
            </Text>
            <HStack align="center" gap={1}>
              <LucideIcon
                name="Info"
                color="neutral.500"
                width={{
                  base: 4,
                  tablet: 5,
                }}
                height={{
                  base: 4,
                  tablet: 5,
                }}
              />
              <Text
                textStyle={{
                  base: 'paragraph.xsmall',
                  tablet: 'paragraph.small',
                }}
                fontWeight="medium"
                color="neutral.500"
              >
                All the reviews in SkinSeoul are verified.
              </Text>
            </HStack>
          </VStack>
          {!hasReviews && !hasAiReviews && <ReviewEmpty />}
        </VStack>
        <Button
          onClick={handleOpenWriteModal}
          disabled={isCreatingReview}
          bg="primary.600"
          h={12}
          w={{
            base: 'full',
            tablet: '180px',
          }}
        >
          Write a review
        </Button>
      </VStack>
      {hasReviews && (
        <>
          <Divider />
          <ReviewHeader
            averageRating={averageRating}
            totalReviews={totalReviewsLength}
            ratingDistribution={ratingDistribution}
            customerImages={customerReviewImages}
            reviews={reviews}
            onViewAllImages={handleViewAllImages}
            onWriteReview={handleOpenWriteModal}
            isWriteReviewDisabled={isCreatingReview}
          />
        </>
      )}

      {hasReviews && !isReviewsLoading && (
        <>
          <ReviewImageGallery images={customerReviewImages} onViewAll={handleViewAllImages} />
          <Divider />
          <VStack
            align="start"
            gap={3}
            mt={{
              base: 6,
              tablet: 8,
            }}
          >
            <Text
              textStyle={{
                base: 'label.medium',
                tablet: 'label.large',
              }}
              fontWeight="medium"
            >
              Filters
            </Text>
            <ReviewFilterBar
              onSortChange={handleSortChange}
              onSkinTypeChange={handleSkinTypeChange}
              onSkinConcernChange={handleSkinConcernChange}
            />
          </VStack>
        </>
      )}

      {!isReviewsLoading && (hasReviews || hasAiReviews) && (
        <VStack
          gap="14px"
          align="start"
          mt={{
            base: 6,
            tablet: 8,
          }}
        >
          <Text
            textStyle={{
              base: 'label.medium',
              tablet: 'heading.h6',
            }}
            fontWeight="medium"
          >
            Reviews
          </Text>
          {(hasReviews || (hasAiReviews && showAiReviews)) && (
            <VStack gap="16px" alignSelf="start" w="full">
              {displayedReviews.map((review, columnIndex, columnArray) => (
                <ReviewCard
                  key={review.id}
                  review={review}
                  onImageClick={handleImageClick}
                  onViewMore={handleViewMore}
                  isLastInColumn={columnIndex === columnArray.length - 1}
                />
              ))}
              {hasAiReviews && showAiReviews && (
                <LoggingImpression
                  logName="ai_review_section_expand"
                  params={{
                    productId: product.productId,
                    name: product.name,
                    price:
                      ('salePrice' in product && product.salePrice) ||
                      ('originalPrice' in product && product.originalPrice) ||
                      0,
                    categories: product.category,
                    variantId: product.productId,
                    itemId: product.productId,
                    pageType: 'Product Detail Page',
                    section: 'AI Review Widget',
                    metadata: {
                      source: 'SkinSight™',
                      component: 'AI Review Section',
                      loadType: 'inline_replacement',
                    },
                  }}
                >
                  <VStack gap="16px" alignSelf="start" w="full">
                    {aiReviews.map((review, columnIndex, columnArray) => (
                      <ReviewCard
                        key={`ai_${review.id}`}
                        review={review}
                        onImageClick={handleImageClick}
                        onViewMore={handleViewMore}
                        isLastInColumn={columnIndex === columnArray.length - 1}
                      />
                    ))}
                  </VStack>
                </LoggingImpression>
              )}
            </VStack>
          )}
        </VStack>
      )}

      {!isReviewsLoading && hasReviews && hasMoreReviews && (
        <Flex
          align="center"
          justify="center"
          w="full"
          mt={{
            base: 6,
            tablet: 8,
          }}
        >
          <LoadMoreButton onClick={handleLoadMore} />
        </Flex>
      )}
      {canShowAIButton && !hasReviews && (
        <>
          <Image
            src={reviewPreviewDesktop.src}
            alt="AI Review Preview"
            width="100%"
            height="auto"
            objectFit="cover"
            display={{ base: 'none', tablet: 'block' }}
          />
          <Image
            src={reviewPreviewMobile.src}
            alt="AI Review Preview"
            width="100%"
            height="auto"
            objectFit="cover"
            display={{ base: 'block', tablet: 'none' }}
          />
        </>
      )}

      {canShowAIButton && (
        <LoggingImpression
          logName="ai_review_button_impression"
          params={{
            productId: product.productId,
            name: product.name,
            price:
              ('salePrice' in product && product.salePrice) ||
              ('originalPrice' in product && product.originalPrice) ||
              0,
            categories: product.category,
            variantId: product.productId,
            itemId: product.productId,
            pageType: 'Product Detail Page',
            section: 'AI Review Widget',
            metadata: {
              source: 'SkinSight™',
              component: 'AI Review Button',
            },
          }}
        >
          <VStack align="center" justify="center" w="full" mt={4}>
            <LoggingClick
              logName="ai_review_button_click"
              params={{
                productId: product.productId,
                name: product.name,
                price:
                  ('salePrice' in product && product.salePrice) ||
                  ('originalPrice' in product && product.originalPrice) ||
                  0,
                categories: product.category,
                variantId: product.productId,
                itemId: product.productId,
                pageType: 'Product Detail Page',
                section: 'AI Review Widget',
                metadata: {
                  source: 'SkinSight™',
                  component: 'AI Review Button',
                },
                buttonText: 'View SkinSight™ Curated Reviews',
              }}
            >
              <Button
                onClick={handleShowAIReviews}
                disabled={isFetchingAIReviews}
                variant="outline"
                w="full"
                h={12}
                border="1px solid #3D1690"
                bg="generic.0"
                color="primary.500"
                textStyle="label.medium"
                fontWeight="normal"
                borderRadius="8px"
              >
                {isFetchingAIReviews ? 'Loading View SkinSight™ Curated Reviews' : 'View SkinSight™ Curated Reviews'}
              </Button>
            </LoggingClick>
            <HStack gap={1} align="flex-start">
              <LucideIcon pt={1} name="Info" color="neutral.500" width={4} height={4} />
              <Text
                textStyle={{
                  base: 'paragraph.xsmall',
                  tablet: 'paragraph.small',
                }}
                fontWeight="regular"
                color="neutral.500"
              >
                SkinSight™ is our proprietary AI ranking system built from millions of real Korean skincare reviews.
              </Text>
            </HStack>
          </VStack>
        </LoggingImpression>
      )}

      {isPC && (
        <ReviewWritePcModal
          isOpen={isPcWriteModalOpen}
          productInfo={{
            name: product.name || '',
            brand: product.brand || '',
            image: product.productImageUrls[0] || '',
            category: product.category[0] || '',
          }}
          onClose={handleCloseWriteModal}
          onSubmit={handleSubmit}
        />
      )}
      {!isPC && (
        <ReviewWriteMobileModal
          isOpen={isMobileWriteOpen}
          productInfo={{
            name: product.name || '',
            brand: product.brand || '',
            image: product.productImageUrls[0] || '',
            category: product.category[0] || '',
          }}
          onClose={handleCloseMobileWriteModal}
          onSubmit={handleSubmit}
        />
      )}
      <ReviewDetailModal
        isOpen={isImageGalleryOpen}
        onClose={handleCloseImageGallery}
        images={customerReviewImages}
        reviews={reviews}
        initialSelectedReview={imageGalleryInitialState.selectedReview}
        initialSelectedImageId={imageGalleryInitialState.selectedImageId}
      />
    </Container>
  );
};

export default ReviewSection;
